<template>
  <MainLayout :fluid="true">
    <div class="grid min-h-[calc(100vh-220px)] grid-cols-1 lg:grid-cols-[280px_1fr] rounded-2xl border border-slate-200 bg-slate-100 text-[13px] text-slate-700 shadow-sm">
      <aside class="border-r border-slate-800 bg-slate-950 px-4 py-5 text-slate-200">
        <p class="text-[10px] uppercase tracking-[0.22em] text-cyan-300">Banjara User Console</p>
        <h1 class="mt-1 text-xl font-bold text-white">Member Dashboard</h1>
        <p class="mt-1 text-[11px] text-slate-300">Frontend Experience Panel</p>

        <div class="mt-6 space-y-2">
          <div
            v-for="menu in menus"
            :key="menu.label"
            class="rounded-lg border border-slate-800 bg-slate-900/60"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-xs font-semibold text-slate-100 hover:bg-slate-800/80"
              @click="toggleMenu(menu.label)"
            >
              <span>{{ menu.label }}</span>
              <span class="text-[11px]">{{ openMenus[menu.label] ? '▾' : '▸' }}</span>
            </button>

            <div v-if="openMenus[menu.label]" class="space-y-1 px-2 pb-2">
              <a
                v-for="item in menu.items"
                :key="`${menu.label}-${item.key}`"
                :href="item.url"
                class="block rounded-md px-2.5 py-1.5 text-[12px]"
                :class="itemClass(item.key)"
              >
                <span class="flex items-center justify-between gap-2">
                  <span>{{ item.label }}</span>
                  <span
                    v-if="item.badge && item.badge > 0"
                    class="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
                  >
                    {{ item.badge }}
                  </span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </aside>

      <main class="px-4 py-5 sm:px-6 lg:px-7">
        <header class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div>
            <p class="text-[10px] uppercase tracking-[0.18em] text-slate-500">User Analytics</p>
            <h2 class="text-xl font-bold text-slate-900">Welcome, {{ authUserName }}</h2>
          </div>
          <form @submit.prevent="logout">
            <button type="submit" class="rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 transition">
              Logout
            </button>
          </form>
        </header>

        <section class="mt-4">
          <slot />
        </section>

        <div v-if="showChatLauncher">
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-y-4 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-4 opacity-0"
          >
            <section
              v-if="isChatWidgetOpen"
              class="fixed bottom-24 right-4 z-[1000] w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:right-6"
              aria-label="Chat widget"
            >
              <header class="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
                <p class="text-sm font-semibold text-slate-900">Quick Chat</p>
                <div class="flex items-center gap-2">
                  <a href="/chat" class="text-xs font-semibold text-cyan-600 hover:text-cyan-500">Open full chat</a>
                  <button
                    type="button"
                    class="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    @click="closeChatWidget"
                    aria-label="Close chat"
                  >
                    ✕
                  </button>
                </div>
              </header>
              <iframe
                src="/chat?widget=1"
                title="Chat widget"
                class="h-[70vh] min-h-[420px] w-full border-0"
                loading="lazy"
              ></iframe>
            </section>
          </transition>

          <button
            type="button"
            class="fixed bottom-5 right-5 z-[1000] inline-flex h-14 w-14 items-center justify-center rounded-full bg-cyan-500 text-white shadow-lg shadow-cyan-500/30 transition hover:scale-105 hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
            :aria-label="isChatWidgetOpen ? 'Close chat' : 'Open chat'"
            :title="isChatWidgetOpen ? 'Close chat' : 'Open chat'"
            @click="toggleChatWidget"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-7 w-7">
              <path d="M4.5 5.25A2.25 2.25 0 0 1 6.75 3h10.5a2.25 2.25 0 0 1 2.25 2.25v8.25a2.25 2.25 0 0 1-2.25 2.25H12l-3.75 3v-3H6.75A2.25 2.25 0 0 1 4.5 13.5V5.25Z" />
            </svg>
            <span
              v-if="liveChatUnreadCount > 0 && !isChatWidgetOpen"
              class="absolute -right-1 -top-1 inline-flex min-h-[1.25rem] min-w-[1.25rem] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white"
            >
              {{ liveChatUnreadCount > 99 ? '99+' : liveChatUnreadCount }}
            </span>
          </button>
        </div>
      </main>
    </div>
  </MainLayout>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import MainLayout from '@/Layouts/MainLayout.vue';

const props = defineProps({
  activeModule: {
    type: String,
    default: 'overview',
  },
  showChatLauncher: {
    type: Boolean,
    default: true,
  },
});

const page = usePage();
const authUserName = computed(() => page.props?.auth?.user?.name || 'Member');
const activeKey = computed(() => String(props.activeModule || 'overview').toLowerCase());
const chatUnreadCount = computed(() => Number(page.props?.auth?.chatUnreadCount || 0));
const liveChatUnreadCount = ref(chatUnreadCount.value);
const authUserId = computed(() => Number(page.props?.auth?.user?.id || 0));
let unreadPollTimer = null;
let userChannel = null;
const isChatWidgetOpen = ref(false);

const toggleChatWidget = () => {
  isChatWidgetOpen.value = !isChatWidgetOpen.value;
};

const closeChatWidget = () => {
  isChatWidgetOpen.value = false;
};

const refreshUnreadCount = async () => {
  try {
    const response = await window.axios.get('/chat/unread-count');
    liveChatUnreadCount.value = Number(response?.data?.unread_count || 0);
  } catch (error) {
    // Ignore polling errors on pages where auth state may change.
  }
};

const menus = computed(() => [
  {
    label: 'Profile',
    items: [
      { key: 'overview', label: 'Dashboard Overview', url: '/dashboard' },
      { key: 'my-profile', label: 'My Profile', url: '/profiles' },
      { key: 'browse', label: 'Browse Profiles', url: '/browse' },
    ],
  },
  {
    label: 'Match Results',
    items: [
      { key: 'requests', label: 'Request Center', url: '/dashboard/requests' },
      { key: 'chat', label: 'Premium Chat', url: '/chat', badge: liveChatUnreadCount.value },
      { key: 'kundli-matching', label: 'Kundli Matching', url: '/kundli-history' },
      { key: 'kundli-history', label: 'Kundli History', url: '/kundli-history' },
    ],
  },
  {
    label: 'Membership',
    items: [
      { key: 'pricing', label: 'Plans & Pricing', url: '/pricing' },
    ],
  },
]);

const openMenus = reactive(
  Object.fromEntries(
    menus.value.map((menu) => {
      const containsActiveItem = menu.items.some((item) => String(item.key || '').toLowerCase() === activeKey.value);
      return [menu.label, containsActiveItem];
    })
  )
);

const toggleMenu = (label) => {
  openMenus[label] = !openMenus[label];
};

const itemClass = (key) => {
  if (activeKey.value === String(key || '').toLowerCase()) {
    return 'bg-cyan-500/20 text-cyan-200';
  }

  return 'text-slate-300 hover:bg-slate-800 hover:text-white';
};

const logout = () => {
  router.post('/logout');
};

onMounted(() => {
  refreshUnreadCount();
  unreadPollTimer = setInterval(refreshUnreadCount, 8000);

  if (window.Echo && authUserId.value > 0) {
    userChannel = window.Echo.private(`user.${authUserId.value}`);
    userChannel.listen('.chat.unread.updated', (event) => {
      liveChatUnreadCount.value = Number(event?.unread_count || 0);
    });
  }
});

onBeforeUnmount(() => {
  if (unreadPollTimer) {
    clearInterval(unreadPollTimer);
    unreadPollTimer = null;
  }

  if (window.Echo && authUserId.value > 0) {
    window.Echo.leave(`private-user.${authUserId.value}`);
  }

  userChannel = null;
});
</script>
